import React from 'react'
import backgroundImage from '../assets/background.jpg'
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Chip,
  useTheme,
  alpha
} from '@mui/material'
import {
  WbSunny,
  ElectricBolt,
  BatteryChargingFull,
  TrendingUp,
  Security,
  Support,
  Nature
} from '@mui/icons-material'
import { Link } from 'react-router-dom'

const HomePage: React.FC = () => {
  const theme = useTheme()

  const features = [
    {
      icon: <Security />,
      title: 'Quality Products',
      description: 'We offer only the best, certified solar products for your peace of mind.'
    },
    {
      icon: <Support />,
      title: 'Expert Support',
      description: 'Our team is here to help you every step of the way, from selection to installation.'
    },
    {
      icon: <Nature />,
      title: 'Sustainable Future',
      description: 'Join us in making the world greener and more sustainable for generations to come.'
    }
  ]

  const quickLinks = [
    { title: 'Products', path: '/products', icon: <WbSunny />, color: '#FF9800' },
    { title: 'Services', path: '/services', icon: <ElectricBolt />, color: '#2196F3' },
    { title: 'Solar Pricing', path: '/solar-pricing', icon: <TrendingUp />, color: '#4CAF50' },
    { title: 'My Orders', path: '/my-orders', icon: <BatteryChargingFull />, color: '#9C27B0' },
  ]

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.5)', // Dark overlay for text readability
            zIndex: 0,
          }
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                lineHeight: 1.2,
              }}
            >
              Power Your Future with{' '}
              <Box component="span" sx={{ color: 'primary.main' }}>
                Starwave Energy
              </Box>
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                opacity: 0.9,
                fontWeight: 400,
                fontSize: { xs: '1.1rem', md: '1.3rem' },
              }}
            >
              Your trusted source for solar products and solutions. Clean energy, bright savings.
            </Typography>
            <Button
              component={Link}
              to="/products"
              variant="contained"
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: theme.shadows[8],
              }}
            >
              Shop Solar Products
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Quick Links Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h4"
          component="h2"
          sx={{ textAlign: 'center', mb: 6, fontWeight: 600 }}
        >
          Explore Our Services
        </Typography>
        <Grid container spacing={3}>
          {quickLinks.map((link) => (
            <Grid item xs={12} sm={6} md={3} key={link.path}>
              <Card
                component={Link}
                to={link.path}
                sx={{
                  textDecoration: 'none',
                  height: '100%',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: theme.shadows[12],
                  },
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      bgcolor: alpha(link.color, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2,
                    }}
                  >
                    <Box sx={{ color: link.color, fontSize: 32 }}>
                      {link.icon}
                    </Box>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {link.title}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Featured Products Section */}
      <Box sx={{ bgcolor: 'background.default', py: 8 }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            component="h2"
            sx={{ textAlign: 'center', mb: 6, fontWeight: 600 }}
          >
            Featured Products
          </Typography>
          <Grid container spacing={4}>
            {/* Placeholder for featured products - will be populated from Firestore */}
            {[1, 2, 3].map((item) => (
              <Grid item xs={12} md={4} key={item}>
                <Card sx={{ height: '100%', position: 'relative' }}>
                  <Chip
                    label="Featured"
                    color="primary"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 16,
                      left: 16,
                      zIndex: 1,
                      fontWeight: 600,
                    }}
                  />
                  <CardMedia
                    component="img"
                    height="240"
                    image={`https://via.placeholder.com/400x240?text=Solar+Product+${item}`}
                    alt={`Solar Product ${item}`}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      Solar Product {item}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      High-quality solar equipment with excellent performance and reliability.
                    </Typography>
                    <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                      PKR {(15000 + item * 5000).toLocaleString()}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button size="small" variant="outlined" fullWidth>
                      View Details
                    </Button>
                    <Button size="small" variant="contained" fullWidth>
                      Add to Cart
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Trust Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <Box sx={{ color: 'primary.main', fontSize: 40 }}>
                    {feature.icon}
                  </Box>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  {feature.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {feature.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Testimonial */}
        <Box sx={{ mt: 8, textAlign: 'center', maxWidth: 600, mx: 'auto' }}>
          <Typography
            variant="h6"
            sx={{
              fontStyle: 'italic',
              color: 'text.secondary',
              mb: 2,
              lineHeight: 1.6,
            }}
          >
            "Starwave Energy helped me save 40% on my energy bills. The installation was quick and the support team is amazing!"
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            - A. Customer
          </Typography>
        </Box>
      </Container>
    </Box>
  )
}

export default HomePage